import React from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';

function SaveConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  onDecline,
  added,
  updated,
  removed,
  detailsNames,
}) {
  if (!isOpen) return null;

  const getDuplicatesText = () => {
    const duplicates = {};

    detailsNames.forEach((detail) => {
      duplicates[detail] = (duplicates[detail] || 0) + 1;
    });

    const duplicateArray = Object.keys(duplicates).map((key) => `${key}: ${duplicates[key]}`);

    const text = duplicateArray.join('\n');
    return text.length >= 400 ? `${text.slice(0, 400)}...` : text;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative bg-background rounded-lg shadow-lg p-6 w-full max-w-md mx-4"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-full bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">Confirm Changes</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Are you sure you want to save these changes?
            </p>
          </div>
        </div>

        {/* Changes Summary */}
        <div className="mt-4 space-y-2">
          {added.length > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-green-500/10">
              <Plus className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-600">
                Added: {added.length} skill(s)
              </span>
            </div>
          )}

          {updated.length > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-blue-500/10">
              <RefreshCw className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">
                Changed: {updated.length} skill(s)
              </span>
            </div>
          )}

          {removed.length > 0 && (
            <div className="p-3 rounded-md bg-red-500/10">
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium text-red-600">
                  Deleted: {detailsNames.length} detail(s)
                </span>
              </div>
              {detailsNames.length > 0 && (
                <pre className="mt-2 text-xs text-red-600/80 whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {getDuplicatesText()}
                </pre>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onDecline} className="text-destructive">
            Decline
          </Button>
          <Button onClick={onConfirm}>Save</Button>
        </div>
      </div>
    </div>
  );
}

SaveConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  added: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updated: PropTypes.arrayOf(PropTypes.string).isRequired,
  removed: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  detailsNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SaveConfirmModal;
